// Shared Photon geocoder access. This logic previously lived inside
// RouteBuilder only, which meant the summit form had no way to look a peak up.
// Both callers now share one implementation and one cache.

const GEOCODER_URL = import.meta.env.VITE_GEOCODER_URL || 'https://photon.komoot.io/api/'
const searchCache = new Map()

export const SEARCH_TYPES = [
  { value: 'all', label: 'Everything' },
  { value: 'airport', label: 'Airports' },
  { value: 'city', label: 'Cities' },
  { value: 'peak', label: 'Peaks' },
]

export function inferKind(properties = {}) {
  const key = String(properties.osm_key || '').toLowerCase()
  const value = String(properties.osm_value || '').toLowerCase()
  if (key === 'aeroway' || value.includes('aerodrome') || value.includes('airport')) return 'airport'
  if (key === 'natural' && ['peak', 'volcano'].includes(value)) return 'summit'
  if (key === 'place' && ['city', 'town', 'village', 'hamlet', 'locality'].includes(value)) return 'city'
  return 'base'
}

export function resultContext(properties = {}) {
  const parts = [properties.city, properties.county, properties.state, properties.country]
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
  return parts.join(' · ')
}

export function resultLabel(properties = {}) {
  const code = properties.extra?.iata || properties.extra?.icao || properties.extra?.ref
  const name = properties.name || properties.city || properties.locality || properties.country || 'Unnamed place'
  return code && !name.toUpperCase().includes(String(code).toUpperCase()) ? `${name} (${code})` : name
}

// Photon exposes elevation for many OSM peaks, but not all of them. A missing
// value simply leaves the elevation field for you to fill in.
function resultElevation(properties = {}) {
  const raw = properties.extra?.ele ?? properties.ele ?? properties.extra?.elevation
  const value = Number(String(raw ?? '').replace(/[^0-9.-]/g, ''))
  return Number.isFinite(value) && value !== 0 ? Math.round(value) : null
}

export function flagFromCountryCode(code) {
  const iso = String(code || '').trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(iso)) return ''
  return String.fromCodePoint(...[...iso].map((character) => 127397 + character.charCodeAt(0)))
}

export class PlaceSearchError extends Error {}

export async function searchPlaces(query, type = 'all') {
  const trimmed = String(query || '').trim()
  if (trimmed.length < 2) throw new PlaceSearchError('Type at least two characters.')

  const cacheKey = `${type}:${trimmed.toLowerCase()}`
  if (searchCache.has(cacheKey)) return searchCache.get(cacheKey)

  const params = new URLSearchParams({ q: trimmed, limit: '8', lang: 'en' })
  if (type === 'airport') params.append('osm_tag', 'aeroway:aerodrome')
  if (type === 'peak') params.append('osm_tag', 'natural:peak')
  if (type === 'city') {
    params.append('layer', 'city')
    params.append('layer', 'locality')
  }

  const response = await fetch(`${GEOCODER_URL}?${params.toString()}`)
  if (!response.ok) throw new Error(`Place search returned ${response.status}`)
  const data = await response.json()

  const results = (data.features || [])
    .filter((feature) => feature?.geometry?.type === 'Point' && Array.isArray(feature.geometry.coordinates))
    .map((feature) => {
      const [longitude, latitude] = feature.geometry.coordinates
      const properties = feature.properties || {}
      return {
        key: `${properties.osm_type || 'x'}-${properties.osm_id || `${longitude}-${latitude}`}`,
        label: resultLabel(properties),
        context: resultContext(properties),
        kind: type === 'airport' ? 'airport' : type === 'peak' ? 'summit' : type === 'city' ? 'city' : inferKind(properties),
        latitude: Number(latitude),
        longitude: Number(longitude),
        countryName: properties.country || '',
        countryCode: String(properties.countrycode || '').toUpperCase(),
        elevation: resultElevation(properties),
      }
    })
    .filter((result, index, list) => list.findIndex((item) => item.key === result.key) === index)

  searchCache.set(cacheKey, results)
  return results
}
